import { useEffect, useState, useCallback, useMemo } from "react";
import api, { fileUrl, fmtIDR } from "@/lib/api";
import { InstallmentButton } from "@/components/InstallmentDialog";
import { PageHeader, Loading, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, AlertTriangle } from "lucide-react";

const REGIONS = ["iBox / Resmi Indonesia", "Inter WiFi Only", "Inter All Operator", "Beacukai"];
const CONDITIONS = ["Mulus", "Lecet Halus", "Lecet Parah"];
const EMPTY_SUPPLIER = "none";
const regionTone = (r) => r?.startsWith("iBox") ? "bg-green-100 text-green-700 border-green-200"
  : r?.includes("Beacukai") ? "bg-sky-100 text-sky-700 border-sky-200"
  : "bg-amber-100 text-amber-700 border-amber-200";

const BLANK = {
  name: "", brand: "", category: "Handphone", supplier_id: EMPTY_SUPPLIER, color: "",
  condition: "", battery_health: "", region: "", sku: "", imei: "",
  cost_price: 0, sell_price: 0, description: "", image_url: "",
};

export default function Products() {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [bs, setBs] = useState({});          // {branchId: {stock, min_stock, active}}
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/products", { params: q ? { q } : {} }).then(({ data }) => setRows(data)).finally(() => setLoading(false));
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    api.get("/branches").then(({ data }) => setBranches(data));
    api.get("/suppliers").then(({ data }) => setSuppliers(data)).catch(() => setSuppliers([]));
  }, []);

  const supName = (id) => (id && id !== EMPTY_SUPPLIER ? suppliers.find((s) => s.id === id)?.name || "-" : "-");

  const displayed = useMemo(() => {
    if (branchFilter === "all") return rows;
    const bstock = (p) => (p.branch_breakdown || []).find((x) => x.branch_id === branchFilter)?.stock || 0;
    return [...rows].sort((a, b) => (bstock(b) > 0) - (bstock(a) > 0) || bstock(b) - bstock(a) || a.name.localeCompare(b.name));
  }, [rows, branchFilter]);

  const initBs = (row) => {
    const map = {};
    branches.forEach((b) => {
      const found = (row?.branch_breakdown || []).find((x) => x.branch_id === b.id);
      map[b.id] = { stock: found?.stock ?? 0, min_stock: found?.min_stock ?? 1, active: found ? found.configured : true };
    });
    return map;
  };

  const openCreate = () => { setEditing(null); setForm(BLANK); setBs(initBs(null)); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...BLANK, ...row, supplier_id: row.supplier_id || EMPTY_SUPPLIER });
    setBs(initBs(row));
    setOpen(true);
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); setUploading(true);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, image_url: data?.url || data?.path || data })); toast.success("Foto diunggah");
    } catch { toast.error("Gagal upload foto"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name) return toast.error("Nama produk wajib diisi");
    setSaving(true);
    const master = {
      ...form,
      supplier_id: form.supplier_id === EMPTY_SUPPLIER ? null : form.supplier_id,
      supplier_name: supName(form.supplier_id),
      cost_price: Number(form.cost_price || 0), sell_price: Number(form.sell_price || 0),
    };
    const branch_stocks = {};
    Object.entries(bs).forEach(([bid, v]) => {
      if (v.active) branch_stocks[bid] = { stock: Number(v.stock || 0), min_stock: Number(v.min_stock || 1) };
    });
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, master);
        await Promise.all(Object.entries(branch_stocks).map(([bid, v]) =>
          api.put(`/products/${editing.id}/branch-stock`, { branch_id: bid, stock: v.stock, min_stock: v.min_stock })));
      } else {
        await api.post("/products", { ...master, branch_scope: "all", branch_stocks });
      }
      toast.success(editing ? "Produk diperbarui" : "Produk ditambahkan");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const del = async (row) => {
    if (!window.confirm("Hapus produk ini?")) return;
    try { await api.delete(`/products/${row.id}`); toast.success("Dihapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  const statusOf = (r) => (r.total_stock > 0 ? { t: "Tersedia", c: "bg-green-100 text-green-700" } : { t: "Habis", c: "bg-red-100 text-red-700" });

  return (
    <div>
      <PageHeader title="Produk" subtitle="Master produk & stok per cabang">
        <Button onClick={openCreate} className="bg-sky-600 hover:bg-sky-700" data-testid="crud-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Tambah</Button>
      </PageHeader>

      <div className="mb-4 flex flex-wrap gap-2 items-end">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / SKU / IMEI..." className="pl-9" data-testid="crud-search" />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500 block mb-1">Filter Cabang</Label>
          <Select value={branchFilter} onValueChange={setBranchFilter}>
            <SelectTrigger className="h-10 w-48" data-testid="product-branch-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Cabang</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            {["Foto", "Nama", "Brand", "Region", "Total", ...branches.map((b) => b.name.replace("Cabang ", "")), "Status", "Modal", "Jual", "Aksi"].map((h, i) => (
              <th key={i} className="px-3 py-3 text-left font-medium text-slate-500 whitespace-nowrap">{h}</th>
            ))}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td colSpan={11}><Loading /></td></tr>
              : displayed.length === 0 ? <tr><td colSpan={11}><Empty /></td></tr>
              : displayed.map((r) => {
                const st = statusOf(r);
                return (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`product-row-${r.id}`}>
                    <td className="px-3 py-3">{r.image_url ? <img src={fileUrl(r.image_url)} alt="" className="w-10 h-10 rounded-lg object-cover border border-slate-200" /> : <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-300 text-[9px]">No Img</div>}</td>
                    <td className="px-3 py-3"><div className="font-medium">{r.name}</div><div className="text-[11px] text-slate-400">{[r.color, r.condition, r.battery_health && `BH ${r.battery_health}%`].filter(Boolean).join(" · ")}</div></td>
                    <td className="px-3 py-3">{r.brand || "-"}</td>
                    <td className="px-3 py-3">{r.region ? <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${regionTone(r.region)}`}>{r.region}</span> : "-"}</td>
                    <td className="px-3 py-3 font-mono-num font-semibold text-slate-800">{r.total_stock}</td>
                    {branches.map((b) => {
                      const bb = (r.branch_breakdown || []).find((x) => x.branch_id === b.id);
                      const configured = bb && bb.configured;
                      const st = bb?.stock ?? 0;
                      const hl = branchFilter === b.id ? "bg-sky-50" : "";
                      return <td key={b.id} className={`px-3 py-3 font-mono-num ${hl} ${!configured ? "text-amber-500" : st <= (bb?.min_stock || 0) ? "text-red-600 font-medium" : "text-slate-700"}`} data-testid={`stock-${r.id}-${b.id}`} title={!configured ? "Belum dikonfigurasi (stok 0) — klik Edit untuk aktifkan" : ""}>{st}{!configured && <AlertTriangle className="w-3 h-3 inline ml-0.5 text-amber-500" />}</td>;
                    })}
                    <td className="px-3 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${st.c}`}>{st.t}</span></td>
                    <td className="px-3 py-3 font-mono-num text-right">{r.cost_price != null ? fmtIDR(r.cost_price) : "•••"}</td>
                    <td className="px-3 py-3 font-mono-num text-right">{fmtIDR(r.sell_price)}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <InstallmentButton product={r} />
                      <button onClick={() => openEdit(r)} data-testid={`edit-${r.id}`} className="p-1.5 text-slate-500 hover:text-sky-600"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(r)} data-testid={`delete-${r.id}`} className="p-1.5 text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg w-full">
          <div className="max-h-[90vh] w-full flex flex-col">
            <DialogHeader className="flex-shrink-0"><DialogTitle>{editing ? "Edit" : "Tambah"} Produk</DialogTitle></DialogHeader>
            <div className="flex-1 overflow-y-auto py-2 pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 flex items-center gap-3">
                  {form.image_url ? <img src={fileUrl(form.image_url)} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200" /> : <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center text-slate-300 text-[10px]">No Img</div>}
                  <input type="file" accept="image/*" onChange={uploadImage} className="text-xs" data-testid="field-image_url" />
                  {uploading && <span className="text-xs text-slate-400">Mengupload...</span>}
                </div>
                <F l="Nama Produk *" full v={form.name} on={(v) => setForm({ ...form, name: v })} test="field-name" />
                <F l="Brand" v={form.brand} on={(v) => setForm({ ...form, brand: v })} test="field-brand" />
                <F l="Kategori" v={form.category} on={(v) => setForm({ ...form, category: v })} test="field-category" />
                <div>
                  <Label className="text-xs text-slate-500">Supplier</Label>
                  <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="field-supplier_id"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value={EMPTY_SUPPLIER}>— Tanpa Supplier —</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <F l="Warna" v={form.color} on={(v) => setForm({ ...form, color: v })} test="field-color" />
                <Sel l="Kondisi Fisik" v={form.condition} opts={CONDITIONS} on={(v) => setForm({ ...form, condition: v })} test="field-condition" />
                <F l="Battery Health %" v={form.battery_health} on={(v) => setForm({ ...form, battery_health: v })} test="field-battery_health" />
                <Sel l="Region / Garansi" v={form.region} opts={REGIONS} on={(v) => setForm({ ...form, region: v })} test="field-region" />
                <F l="SKU" v={form.sku} on={(v) => setForm({ ...form, sku: v })} test="field-sku" />
                <F l="IMEI" v={form.imei} on={(v) => setForm({ ...form, imei: v })} test="field-imei" />
                <F l="Harga Modal" type="number" v={form.cost_price} on={(v) => setForm({ ...form, cost_price: v })} test="field-cost_price" />
                <F l="Harga Jual" type="number" v={form.sell_price} on={(v) => setForm({ ...form, sell_price: v })} test="field-sell_price" />
                <F l="Deskripsi" full v={form.description} on={(v) => setForm({ ...form, description: v })} test="field-description" />

                <div className="col-span-2 mt-1 border-t pt-3">
                  <Label className="text-xs font-semibold text-slate-700">Stok per Cabang</Label>
                  <p className="text-[11px] text-slate-400 mb-2">Aktifkan cabang agar produk tersedia untuk transaksi (stok 0 tetap tampil).</p>
                  <div className="space-y-2">
                    {branches.map((b) => {
                      const v = bs[b.id] || { stock: 0, min_stock: 1, active: true };
                      return (
                        <div key={b.id} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2">
                          <label className="flex items-center gap-1.5 text-sm flex-1">
                            <input type="checkbox" checked={v.active} onChange={(e) => setBs({ ...bs, [b.id]: { ...v, active: e.target.checked } })} data-testid={`bs-active-${b.id}`} />
                            {b.name}
                          </label>
                          <div><span className="text-[10px] text-slate-400 block">Stok</span><Input type="number" disabled={!v.active} value={v.stock} onChange={(e) => setBs({ ...bs, [b.id]: { ...v, stock: e.target.value } })} className="h-8 w-20" data-testid={`bs-stock-${b.id}`} /></div>
                          <div><span className="text-[10px] text-slate-400 block">Min</span><Input type="number" disabled={!v.active} value={v.min_stock} onChange={(e) => setBs({ ...bs, [b.id]: { ...v, min_stock: e.target.value } })} className="h-8 w-16" /></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="flex-shrink-0 border-t pt-3">
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="crud-save-btn">{saving ? "Menyimpan..." : "Simpan"}</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ l, v, on, type = "text", full, test }) => (
  <div className={full ? "col-span-2" : ""}>
    <Label className="text-xs text-slate-500">{l}</Label>
    <Input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="mt-1" data-testid={test} />
  </div>
);
const Sel = ({ l, v, opts, on, test }) => (
  <div>
    <Label className="text-xs text-slate-500">{l}</Label>
    <Select value={v || ""} onValueChange={on}><SelectTrigger className="mt-1" data-testid={test}><SelectValue placeholder="Pilih" /></SelectTrigger>
      <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
  </div>
);
